import React, { useEffect, useState } from 'react';

import { Paper, Stack, Typography } from '@mui/material';

import type { ManualApprovalState } from '../types';
import type { CampaignChecklistRow } from '../utils';

import ChecklistRuleCard from './ChecklistRuleCard';
import HiddenApprovedFooter from './HiddenApprovedFooter';
import ReportCard from './ReportCard';

interface Props {
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<CampaignChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
}

export default function CampaignReportCard({
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedRows(rows.reduce<Record<string, boolean>>((nextRows, row) => ({ ...nextRows, [row.id]: true }), {}));
  }, [rows]);

  return (
    <ReportCard>
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {rows.length === 0 ? (
          <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
            <Typography variant="body2">No Campaign rows match this view.</Typography>
          </Paper>
        ) : (
          rows.map((row) => {
            const manuallyApproved = !!manualApprovals[row.id];
            const approved = row.status === 'approved' || manuallyApproved;
            const isExpanded = expandedRows[row.id] ?? true;

            return (
              <ChecklistRuleCard
                key={row.id}
                approved={approved}
                approvalNote={row.approvalNote}
                dataPoints={row.dataPoints}
                expanded={isExpanded}
                manuallyApproved={manuallyApproved}
                rule={row.rule}
                summary={row.summary}
                title={row.packageName ? `${row.field}: ${row.packageName}` : row.field}
                onApprovalChange={() => onApprovalChange(row.id)}
                onToggleExpanded={() => setExpandedRows((currentRows) => ({ ...currentRows, [row.id]: !isExpanded }))}
              />
            );
          })
        )}
      </Stack>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="Campaign row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
